import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  TextField,
  TextFieldProps
} from '@mui/material'
import { ChangeEvent, SyntheticEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type HeatingPowerOption = {
  label: string
  value: number
}

export interface HeatingPowerAutocompleteProps
  extends Omit<TextFieldProps, 'children' | 'onChange'> {
  value: number | undefined
  onChange: (value: number | undefined) => void
}

export default function HeatingPowerAutocomplete({
  value,
  onChange,
  ...props
}: HeatingPowerAutocompleteProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('heater')

  /**
   * The options.
   */
  const options: HeatingPowerOption[] = [
    { label: '15 kW', value: 15 },
    { label: '30 kW', value: 30 },
    { label: '50 kW', value: 50 },
    { label: '100 kW', value: 100 },
    { label: '200 kW', value: 200 },
    { label: '250 kW', value: 250 }
  ]

  /**
   * The input value.
   */
  const [inputValue, setInputValue] = useState<string>(value === undefined ? '' : `${value}`)

  /**
   * The selected option.
   */
  const selectedOption =
    (options.find((option) => option.value === value) ?? value === undefined) ? null : `${value}kW`

  const handleChange = (
    _event: SyntheticEvent,
    option: string | HeatingPowerOption | null,
    _reason: AutocompleteChangeReason
  ) => {
    if (typeof option === 'string') {
      if (option !== '' && !isNaN(parseInt(option))) {
        onChange(parseInt(option))
      }
    } else if (option !== null) {
      onChange(option.value)
    }
  }

  const handleInputChange = (
    _event: SyntheticEvent,
    _value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // Handle the event when the user clears the input
    // using the clear button.
    if (reason === 'clear') {
      onChange(undefined)
    }
  }

  const handleTextFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace('kW', '').trim()

    if (value === '') {
      onChange?.(undefined)
    } else if (!isNaN(parseInt(value))) {
      onChange?.(parseInt(value))
    }
  }

  /**
   * Set the input value when value changes.
   */
  useEffect(() => {
    if (value === undefined) {
      setInputValue('')
    } else {
      setInputValue(`${value}`)
    }
  }, [value])

  return (
    <Autocomplete
      freeSolo
      selectOnFocus
      clearOnBlur
      options={options}
      value={selectedOption}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      disabled={props.disabled}
      renderInput={(params) => (
        <TextField
          label={t('heatingPower')}
          {...params}
          {...props}
          // inputProps={{
          //   inputMode: 'numeric'
          // }}
          // InputProps={{
          //   endAdornment: 'kW'
          // }}
          onChange={handleTextFieldChange}
        />
      )}
    />
  )
}
